/*
  STR8 MARQUEE v1.0

  Drives the spec strip and makes it steerable: click or tap to stop and
  start it, drag to scrub through it, arrow keys to nudge.

  WHY THIS REPLACES THE CSS ANIMATION RATHER THAN SITTING ON TOP OF IT
  A CSS keyframe owns the transform outright. Pausing it is easy, but a drag
  has to read a position out of a running animation, take the transform over,
  then hand it back without a jump, and the handover is where that approach
  falls apart. One rAF loop owning a single offset makes pause, drag and
  resume three states of the same number, and there is nothing to hand over.

  The keyframe stays in the stylesheet as the no-JS path. This script adds
  is-js to the track, which switches the animation off, so a visitor whose
  JavaScript never runs still sees a scrolling strip rather than a dead one.

  Speed is derived from the CSS duration rather than hardcoded in pixels, so
  the strip keeps the same pace if items are added or removed.
*/
(function () {
  'use strict';

  if (window.__str8MarqueeLoaded) return;
  window.__str8MarqueeLoaded = true;

  var DRAG_THRESHOLD = 4;   /* px of travel before a press counts as a drag */
  var KEY_NUDGE = 64;

  function Marquee(root) {
    this.root = root;
    this.track = root.querySelector('[data-str8-marquee-track]');
    if (!this.track) return;

    this.duration = parseFloat(root.getAttribute('data-duration')) || 44;
    this.offset = 0;
    this.half = 0;
    this.paused = false;
    this.dragging = false;
    this.moved = false;
    this.visible = true;
    this.last = 0;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reduced) this.paused = true;

    this.track.classList.add('is-js');
    this.measure();

    this.bind();
    this.checkRun();
  }

  /*
    Half the track, because the content is rendered twice. Wrapping at exactly
    that point is what makes the loop seamless, and it only holds while both
    halves are identical, which is why the section renders one list twice
    rather than carrying two hand-written copies.
  */
  Marquee.prototype.measure = function () {
    this.half = this.track.scrollWidth / 2;
    this.speed = this.half > 0 ? this.half / this.duration : 0;
  };

  Marquee.prototype.wrap = function (v) {
    if (!this.half) return 0;
    v = v % this.half;
    if (v > 0) v -= this.half;
    return v;
  };

  Marquee.prototype.apply = function () {
    this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px, 0, 0)';
  };

  /* ---- Input ----------------------------------------------------------- */

  Marquee.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      self.dragging = true;
      self.moved = false;
      self.startX = e.clientX;
      self.startOffset = self.offset;
      self.root.setPointerCapture && self.root.setPointerCapture(e.pointerId);
      self.root.classList.add('is-dragging');
    });

    this.root.addEventListener('pointermove', function (e) {
      if (!self.dragging) return;
      var delta = e.clientX - self.startX;
      if (Math.abs(delta) > DRAG_THRESHOLD) self.moved = true;
      self.offset = self.wrap(self.startOffset + delta);
      self.apply();
    });

    var end = function (e) {
      if (!self.dragging) return;
      self.dragging = false;
      self.root.classList.remove('is-dragging');
      if (self.root.releasePointerCapture && e.pointerId !== undefined) {
        try { self.root.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      }
      /* A press that never travelled is a click, so it toggles instead. */
      if (!self.moved) self.toggle();
    };
    this.root.addEventListener('pointerup', end);
    this.root.addEventListener('pointercancel', end);

    /* The strip is a control now, so it has to be operable without a mouse. */
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        self.offset = self.wrap(self.offset - KEY_NUDGE);
        self.apply();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        self.offset = self.wrap(self.offset + KEY_NUDGE);
        self.apply();
        e.preventDefault();
      } else if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
        self.toggle();
        e.preventDefault();
      }
    });

    /* A drag is a click that moved, so suppress the click that follows one. */
    this.root.addEventListener('click', function (e) {
      if (self.moved) { e.preventDefault(); e.stopPropagation(); }
    });

    window.addEventListener('resize', function () {
      self.measure();
      self.offset = self.wrap(self.offset);
      self.apply();
    });

    document.addEventListener('visibilitychange', function () { self.checkRun(); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.checkRun();
      }, { threshold: 0 }).observe(this.root);
    }
  };

  Marquee.prototype.toggle = function () {
    this.paused = !this.paused;
    this.root.classList.toggle('is-paused', this.paused);
    this.root.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
  };

  /* ---- Loop ------------------------------------------------------------ */

  Marquee.prototype.checkRun = function () {
    var should = this.visible && !document.hidden;
    if (should && !this.raf) {
      var self = this;
      this.last = 0;
      var step = function (now) {
        if (!self.last) self.last = now;
        var dt = (now - self.last) / 1000;
        self.last = now;
        /* Clamped so a backgrounded tab returning does not jump the strip. */
        if (dt > 0.1) dt = 0.1;
        if (!self.paused && !self.dragging) {
          self.offset = self.wrap(self.offset - self.speed * dt);
          self.apply();
        }
        self.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    } else if (!should && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  };

  /* ---- Boot ------------------------------------------------------------ */

  function init() {
    var nodes = document.querySelectorAll('[data-str8-marquee]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__str8Marquee) continue;
      nodes[i].__str8Marquee = new Marquee(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
